import { useQuery } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import React, { useEffect, useMemo, useRef, useState } from "react"

import { AddressDetail, CreateAddressRequest, MexicoAddress, UserAddressRole } from "@lib/clients/nelo-api-client"
import { neloApiClient } from "@lib/config-client"
import NeloButton from "@modules/common/components/nelo-button"
import AddressAutoCompleteField from "@modules/signup-and-order-flow/components/address-auto-complete"
import NeloTextInput from "@modules/signup-and-order-flow/components/common/nelo-text-input"
import { useGetProfile } from "@modules/signup-and-order-flow/util/useGetProfile"

import { MexicanStates } from "../mexican-states"

interface AddressEntryFormProps {
  onSubmit?: (newAddressRequest: CreateAddressRequest) => void
  setShowAddressEntryForm?: (show: boolean) => void
  onAddressChange?: (address: MexicoAddress) => void
  hideSubmitButton?: boolean
}

const AddressEntryForm = ({ onSubmit, setShowAddressEntryForm, onAddressChange, hideSubmitButton = false }: AddressEntryFormProps) => {
  const { profile } = useGetProfile()

  const [address, setAddress] = useState<MexicoAddress>({
    street: "",
    buildingNumber: "",
    colony: "",
    city: "",
    postalCode: "",
    addressedTo: "",
    deliveryInstructions: "",
    reference: "",
    state: "",
    predetermined: true,
    interiorNumber: "",
  })

  const userEditedFields = useRef<Record<string, boolean>>({})

  useEffect(() => {
    if (profile?.address?.addressMX) {
      setAddress({
        street: profile.address.addressMX.street ?? "",
        buildingNumber: profile.address.addressMX.buildingNumber ?? "",
        colony: profile.address.addressMX.colony ?? "",
        city: profile.address.addressMX.city ?? "",
        postalCode: profile.address.addressMX.postalCode ?? "",
        addressedTo: profile.address.addressMX.addressedTo ?? "",
        deliveryInstructions: profile.address.addressMX.deliveryInstructions ?? "",
        reference: profile.address.addressMX.reference ?? "",
        state: profile.address.addressMX.state ?? "",
        predetermined: true,
        interiorNumber: profile.address.addressMX.interiorNumber ?? "",
      })
    }
  }, [profile])

  const [postalCodeDetails, setPostalCodeDetails] = useState<AddressDetail[]>([])

  const debouncedPostalCode = useDebounce(address.postalCode || "", 500)

  useEffect(() => {
    if (/^\d{5}$/.test(debouncedPostalCode)) {
      delete userEditedFields.current.state
      delete userEditedFields.current.city
      delete userEditedFields.current.colony
    }
  }, [debouncedPostalCode])

  const { data: fetchedDetails } = useQuery({
    queryKey: ["postalCodeDetails", debouncedPostalCode],
    queryFn: async () => {
      const response = await neloApiClient.getAddressesOfPostalCode(debouncedPostalCode)
      return response?.details ?? []
    },
    enabled: /^\d{5}$/.test(debouncedPostalCode),
  })

  useEffect(() => {
    const isValidPostalCode = /^\d{5}$/.test(debouncedPostalCode)

    if (!isValidPostalCode) {
      setPostalCodeDetails([])
      return
    }
    if (fetchedDetails) {
      setPostalCodeDetails(fetchedDetails)
    } else {
      setPostalCodeDetails([])
    }
  }, [debouncedPostalCode, fetchedDetails])

  useEffect(() => {
    if (postalCodeDetails.length === 1) {
      const single = postalCodeDetails[0]
      const { state, colony, city } = single

      // Only update fields that the user hasn't manually edited
      const updatedFields: Partial<MexicoAddress> = {}

      if (address.state !== state && !userEditedFields.current.state) {
        updatedFields.state = state
      }

      if (address.colony !== colony && !userEditedFields.current.colony) {
        updatedFields.colony = colony
      }

      if (address.city !== city && !userEditedFields.current.city) {
        updatedFields.city = city
      }

      if (Object.keys(updatedFields).length > 0) {
        setAddress((prev) => {
          const updated = {
            ...prev,
            ...updatedFields,
          }
          onAddressChange?.(updated)
          return updated
        })
      }
    }
  }, [postalCodeDetails, address, onAddressChange])

  const { uniqueStates, uniqueCities, uniqueColonies } = useMemo(() => {
    const states = new Set<string>()
    const cities = new Set<string>()
    const colonies = new Set<string>()

    postalCodeDetails.forEach(({ state, city, colony }) => {
      states.add(state)
      cities.add(city)
      colonies.add(colony)
    })

    return {
      uniqueStates: Array.from(states),
      uniqueCities: Array.from(cities),
      uniqueColonies: Array.from(colonies),
    }
  }, [postalCodeDetails])

  useEffect(() => {
    if (uniqueStates.length === 1 && address.state !== uniqueStates[0] && !userEditedFields.current.state) {
      setAddress((prev) => ({ ...prev, state: uniqueStates[0] }))
    }
  }, [uniqueStates, address.state])

  useEffect(() => {
    if (uniqueCities.length === 1 && address.city !== uniqueCities[0] && !userEditedFields.current.city) {
      setAddress((prev) => ({ ...prev, city: uniqueCities[0] }))
    }
  }, [uniqueCities, address.city])

  useEffect(() => {
    if (uniqueColonies.length === 1 && address.colony !== uniqueColonies[0] && !userEditedFields.current.colony) {
      setAddress((prev) => ({ ...prev, colony: uniqueColonies[0] }))
    }
  }, [uniqueColonies, address.colony])

  const displayedStates: string[] = uniqueStates.length ? uniqueStates : [...MexicanStates]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    userEditedFields.current[name] = true

    setAddress((prev) => {
      const updated = { ...prev, [name]: value }
      onAddressChange?.(updated)
      return updated
    })
  }

  const disableButton =
    !address.street ||
    !address.buildingNumber ||
    !address.colony ||
    !address.city ||
    !address.postalCode ||
    !address.addressedTo ||
    !address.reference ||
    !address.state

  const createAddressRequest = (): CreateAddressRequest => {
    return {
      address: address,
      role: UserAddressRole.DELIVERY,
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (disableButton) return
    if (onSubmit) {
      onSubmit(createAddressRequest())
      setShowAddressEntryForm?.(false)
    }
  }

  return (
    <div>
      <form className="space-y-4" onSubmit={handleFormSubmit}>
        {!hideSubmitButton && (
          <NeloTextInput
            type="text"
            name="addressedTo"
            placeholder="Nombre de destinatario*"
            required
            value={address.addressedTo}
            onChange={handleInputChange}
          />
        )}

        <NeloTextInput type="text" name="street" placeholder="Calle*" required value={address.street} onChange={handleInputChange} />
        <div className="grid grid-cols-2 gap-4">
          <NeloTextInput
            type="text"
            name="buildingNumber"
            placeholder="Número (exterior)*"
            required
            value={address.buildingNumber}
            onChange={handleInputChange}
          />
          <NeloTextInput
            type="text"
            name="interiorNumber"
            placeholder="Número (interior)"
            value={address.interiorNumber}
            onChange={handleInputChange}
          />
        </div>

        <NeloTextInput
          type="text"
          name="postalCode"
          placeholder="Código Postal*"
          required
          value={address.postalCode}
          onChange={handleInputChange}
        />

        <AddressAutoCompleteField
          name="state"
          placeholder="Estado*"
          required
          options={displayedStates}
          value={address.state || ""}
          onChange={handleInputChange}
        />

        <AddressAutoCompleteField
          name="colony"
          placeholder="Colonia*"
          required
          options={uniqueColonies}
          value={address.colony || ""}
          onChange={handleInputChange}
        />

        <AddressAutoCompleteField
          name="city"
          placeholder="Ciudad*"
          required
          options={uniqueCities}
          value={address.city || ""}
          onChange={handleInputChange}
        />

        <NeloTextInput
          type="text"
          name="reference"
          placeholder="Entre calles"
          required
          value={address.reference}
          onChange={handleInputChange}
        />

        <NeloTextInput
          type="text"
          name="deliveryInstructions"
          placeholder="Instrucciones para mensajero"
          value={address.deliveryInstructions}
          onChange={handleInputChange}
        />

        {!hideSubmitButton && (
          <NeloButton className={`mt-6 w-full ${disableButton ? "bg-ui-fg-muted hover:bg-ui-fg-muted" : ""}`} disabled={disableButton}>
            Guardar
          </NeloButton>
        )}
      </form>
    </div>
  )
}

export default AddressEntryForm
